import React, { PureComponent, useState } from "react";
import WrappedTooltip, { TooltipRoot } from "../common/WrappedTooltip";
import styled from "styled-components/macro";
import { DRAG_LINK, DRAG_MOVE, DRAG_RESIZE_END, DRAG_RESIZE_START, TASK_HEIGHT_LOOSE, ZOOM_MODE_DAY } from "../../helpers/Const";
import { connect } from "react-redux";
import { hexToRGB, brightenHexColor, pickTextColorBasedOnBgColorSimple } from "../../helpers/StyleHelpers";
import { snapMouseMoveBasedOnZoomMode } from "../../helpers/MiscUtils";
import { addHours, addDays, startOfDay } from "date-fns";
import { formatWithLocale as format } from "../../helpers/DateHelpers";
import { ensureStartAndEnd } from "../../../backend/helpers/dateHelpers";
import { iRootState, Dispatch } from "src/rematch/store";
import { ILinkConfigurationItem, IObjectTypeConfigurationItem } from "src/backend/interfaces/configuration/IConfiguration";
import { IItem } from "src/backend/interfaces/IItem";
import { Action } from "@rematch/core";

export const LINKER_DIAMETER = 20;
export const LINKER_DIAMETER_INNER = 10;
export const LINKER_DISTANCE = 8;
const RESIZER_WIDTH = 14;
const LINKER_BORDER_WIDTH = 2;
const DATA_TASK_SIDE_EXTRA = 20;

interface IDataTaskContentProps {
  useFullOpacity: boolean;
  sidePadding: number;
  color: string;
}

const DataTaskContent = styled.div`
  color: ${(props: IDataTaskContentProps) => props.color};
  opacity: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex: 1 1 auto;
  padding-left: ${(props: IDataTaskContentProps) => props.sidePadding}px;
  padding-right: ${(props: IDataTaskContentProps) => props.sidePadding}px;
  justify-content: start;
  display: flex;
`;

const DataTaskResizer = styled.div`
  width: ${RESIZER_WIDTH}px;
  margin-left: auto;
  height: 100%;
  display: flex;
  cursor: col-resize;
  justify-content: center;
  align-items: center;
`;

const DataTaskResizerMarker = styled.div`
  flex: 0 0 3px;
  height: 80%;
  border-left: 1px solid #ffffff;
  border-right: 1px solid #ffffff;
  opacity: 0.4;
`;

interface IDataTaskLinkerRootProps {
  left: number;
}

const DataTaskLinkerRoot = styled.div`
  left: ${(props: IDataTaskLinkerRootProps) => props.left}px;
  position: absolute;
  width: ${LINKER_DIAMETER}px;
  height: ${LINKER_DIAMETER}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  top: 0px;
`;

interface IDataTaskLinkerTargetProps {
  clearCirclefill: boolean;
  drawCircle: boolean;
}

const DataTaskLinkerTarget = styled.div`
  position: relative;
  width: ${LINKER_DIAMETER_INNER}px;
  height: ${LINKER_DIAMETER_INNER}px;
  border-radius: ${LINKER_DIAMETER_INNER / 2}px;
  background: ${(props: IDataTaskLinkerTargetProps) => (props.drawCircle && !props.clearCirclefill ? "#000000" : "rgba(0, 0, 0, 0)")};
  border: ${(props: IDataTaskLinkerTargetProps) => (props.drawCircle ? `solid ${LINKER_BORDER_WIDTH}px #000000` : undefined)};
`;

interface ITimeTooltipProps {
  left: number;
}

const TimeTooltip = styled(TooltipRoot)`
  left: ${(props: ITimeTooltipProps) => props.left}px;
  position: absolute;
  white-space: nowrap;
`;

const DataTaskRoot = styled.div`
  position: absolute;
`;

interface IDataTaskInnerProps {
  width: number;
  height: number;
  background: string;
  editable: boolean;
}

const DataTaskInner = styled.div`
  width: ${(props: IDataTaskInnerProps) => props.width}px;
  background: ${(props: IDataTaskInnerProps) => props.background};
  height: ${(props: IDataTaskInnerProps) => props.height}px;
  left: ${DATA_TASK_SIDE_EXTRA}px;
  text-align: center;
  align-items: center;
  display: flex;
  position: relative;

  border-radius: ${(props: IDataTaskInnerProps) => props.height / 2}px;
  &:hover {
    cursor: ${(props: IDataTaskInnerProps) => (props.editable ? "grab" : "normal")};
  }
  &:active {
    cursor: ${(props: IDataTaskInnerProps) => (props.editable ? "grabbing" : "normal")};
  }
`;

interface IDataTaskLinkerProps {
  startLink?: (e) => void;
  endLink?: (e) => void;
  left: number;
  drawCircle: boolean;
  isLinkDragUnderway: boolean;
  isDragSource:boolean;
}

function DataTaskLinker(props: IDataTaskLinkerProps) {
  const [isMouseOverLinker, setMouseOverLinker] = useState(false);

  let clearCirclefill: boolean;

  if (props.endLink) {
    clearCirclefill = isMouseOverLinker && props.isLinkDragUnderway && !props.isDragSource;
  } else {
    clearCirclefill = isMouseOverLinker || (props.isLinkDragUnderway && props.isDragSource);
  }
  return (
    <DataTaskLinkerRoot
      onMouseEnter={() => setMouseOverLinker(true)}
      onMouseLeave={() => setMouseOverLinker(false)}
      left={props.left}
      onMouseDown={props.startLink ? props.startLink : undefined}
      onMouseUp={props.endLink ? props.endLink : undefined}
    >
      <DataTaskLinkerTarget drawCircle={props.drawCircle} clearCirclefill={clearCirclefill} />
    </DataTaskLinkerRoot>
  );
}

interface IDataTaskProps {
  isSelected?: boolean;
  taskHeight?: number;
  dateFormat?: string;
  timeFormat?: string;
  showTimeInTable?: boolean;
  linkConfigurations?: ILinkConfigurationItem[];
  getItem?: (itemId: number) => IItem;
  getObjectTypeConfig?: (objectTypeId: number) => IObjectTypeConfigurationItem,
  onSelectItem?: (taskId: number) => Action<number,void>;
  toggleShowEditBox?: () => Action<void,void>;
  onStartDrag?: (e: React.MouseEvent<HTMLElement>, taskId: number, dragType: string) => void;
  checkedOutToSomeoneElse?: boolean;
  task: IItem;
  onEndDrag?: (taskId: number, dragType: string, delta?: number, dragUnderway?: boolean) => void;
  xDelta?: number;
  dragUnderway: boolean;
  minWidthAdjustment: number;
  dragTaskId?: number;
  editable?: boolean;
  dragType?: string;
  left: number;
  width: number;
  dragDeadZoneReached?: boolean;
  zoomMode: string;
  hourWidth?: number;
  index: number;
  color: string;
}

interface IDataTaskState {
  mouseOver: boolean;
}

class DataTask extends PureComponent<IDataTaskProps,IDataTaskState> {
  constructor(props: IDataTaskProps) {
    super(props);
    this.state = {
      mouseOver: false,
    };
  }

  // The setTimeout function calls are made so that the mouseup event is handled after the click event.
  // The click event handler checks if a drag is underway ("dragUnderway" prop) and if so, does nothing.
  startMove = (e) => {
    if (e.buttons === 1 && this.props.onStartDrag && !this.props.checkedOutToSomeoneElse) {
      document.addEventListener("mouseup", this.endMove);
      this.props.onStartDrag(e, this.props.task.id, DRAG_MOVE);
    }
  };

  endMove = (_e) => {
    setTimeout(() => {
      document.removeEventListener("mouseup", this.endMove);
      this.props.onEndDrag(this.props.task.id, DRAG_MOVE, this.props.xDelta, !this.props.dragUnderway);
    });
  };

  startResizingFromBeginning = (e) => {
    if (e.buttons === 1) {
      document.addEventListener("mouseup", this.endResizingFromBeginning);
      this.props.onStartDrag(e, this.props.task.id, DRAG_RESIZE_START);
    }
  };

  endResizingFromBeginning = (_e) => {
    setTimeout(() => {
      document.removeEventListener("mouseup", this.endResizingFromBeginning);
      this.props.onEndDrag(this.props.task.id, DRAG_RESIZE_START, this.props.xDelta, !this.props.dragUnderway);
    });
  };

  startResizingFromEnd = (e) => {
    if (e.buttons === 1) {
      document.addEventListener("mouseup", this.endResizingFromEnd);
      this.props.onStartDrag(e, this.props.task.id, DRAG_RESIZE_END);
    }
  };

  endResizingFromEnd = (_e) => {
    setTimeout(() => {
      document.removeEventListener("mouseup", this.endResizingFromEnd);
      this.props.onEndDrag(
        this.props.task.id,
        DRAG_RESIZE_END,
        this.props.xDelta + this.props.minWidthAdjustment,
        !this.props.dragUnderway
      );
    });
  };

  startLink = (e) => {
    if (e.buttons === 1) {
      document.addEventListener("mouseup", this.endLinkCancel);
      this.props.onStartDrag(e, this.props.task.id, DRAG_LINK);
    }
  };

  endLink = (_e) => {
    setTimeout(() => {
      this.props.onEndDrag(this.props.task.id, DRAG_LINK);
    });
  };

  endLinkCancel = (_e) => {
    setTimeout(() => {
      document.removeEventListener("mouseup", this.endLinkCancel);
      this.props.onEndDrag(this.props.task.id, DRAG_LINK, null, true);
    });
  };

  removeItem = (e) => {
    e.stopPropagation();
  };

  getDateFormat() {
    return this.props.dateFormat + (this.props.showTimeInTable ? " " + this.props.timeFormat : "");
  }

  isLinkFromPossible(objectType) {
    for(const linkConfig of this.props.linkConfigurations) {
      if(linkConfig.from === objectType) {
        return true;
      }
    }
    return false;
  }

  isLinkToPossible(objectType) {
    const dragTask = this.props.getItem(this.props.dragTaskId);
    for(const linkConfig of this.props.linkConfigurations) {
      if(linkConfig.from === dragTask.objectType && linkConfig.to === objectType) {
        return true;
      }
    }
    return false;
  }

  getTaskContents(width, useFullOpacity, adjustedStartTime, adjustedEndTime) {
    let key = 0;
    const padding = 4;
    const objectTypeConfig = this.props.getObjectTypeConfig(this.props.task.objectType);
    let taskText = this.props.task.name;
    if(objectTypeConfig?.gridTextProperty) {
      const textProperty = this.props.task.properties.find(i => i.property === objectTypeConfig?.gridTextProperty);
      if(textProperty) {
        taskText = textProperty.displayValue;
      }
    }
    const contentColor = pickTextColorBasedOnBgColorSimple(this.props.color,'#ffffff','#000000');
    const contents = [
      <DataTaskContent color={contentColor} useFullOpacity={useFullOpacity} key={key++} sidePadding={padding} onMouseDown={this.startMove}>
        {taskText}
      </DataTaskContent>,
    ];

    // Time boxes

    const isRescheduleDragUnderway = this.props.dragUnderway && this.props.dragType !== DRAG_LINK;
    const dateFormat = this.getDateFormat();
    const startTimeText = format(adjustedStartTime, dateFormat);
    const endTimeText = format(adjustedEndTime, dateFormat);
    if (isRescheduleDragUnderway && this.props.task.id === this.props.dragTaskId) {
      contents.unshift(
        <TimeTooltip key={key++} left={-(10 + 7 * (startTimeText.length + 2))}>
          {startTimeText}
        </TimeTooltip>
      );
      contents.push(
        <TimeTooltip key={key++} left={width + 10}>
          {endTimeText}
        </TimeTooltip>
      );
    }

    // Linking tools
    if (this.props.editable && !this.props.checkedOutToSomeoneElse) {
      const isLinkDragUnderway = this.props.dragUnderway && this.props.dragType === DRAG_LINK;
      const isDragSource = this.props.dragTaskId === this.props.task.id;
      let drawSourceCircle = false;
      let drawTargetCircle = false;
      if (!this.props.dragUnderway && (this.state.mouseOver || this.props.isSelected)) {
        if(this.isLinkFromPossible(this.props.task.objectType)) {
          drawSourceCircle = true;
        }
      }
      if (isLinkDragUnderway) {
        if (isDragSource) {
          drawSourceCircle = true;
        } else {
          if(this.isLinkToPossible(this.props.task.objectType)) {
            drawTargetCircle = true;
          }
        }
      }

      contents.unshift(
        <DataTaskLinker
          key={key++}
          drawCircle={drawTargetCircle}
          left={-(LINKER_DIAMETER + LINKER_DISTANCE)}
          endLink={this.endLink}
          isLinkDragUnderway={isLinkDragUnderway}
          isDragSource={isDragSource}
        />
      );
      contents.push(
        <DataTaskLinker
          key={key++}
          drawCircle={drawSourceCircle}
          left={width + LINKER_DISTANCE}
          startLink={this.startLink}
          isLinkDragUnderway={isLinkDragUnderway}
          isDragSource={isDragSource}
        />
      );
    }

    // Resize tools
    if (this.props.editable && !this.props.checkedOutToSomeoneElse && useFullOpacity) {
      contents.unshift(
        <DataTaskResizer key={key++} onMouseDown={this.startResizingFromBeginning} /*floatLeft*/>
          <DataTaskResizerMarker />
        </DataTaskResizer>
      );
      contents.push(
        <DataTaskResizer key={key} onMouseDown={this.startResizingFromEnd}>
          <DataTaskResizerMarker />
        </DataTaskResizer>
      );
    }
    return contents;
  }

  selectItem = (e) => {
    if (!this.props.dragUnderway) {
      if (this.props.isSelected) {
        // If already selected, deselect
        this.props.onSelectItem(null);
      } else {
        this.props.onSelectItem(this.props.task.id);
      }
      e.stopPropagation();
    }
  };

  onMouseEnter = () => {
    this.setState({ mouseOver: true });
  };

  onMouseLeave = () => {
    this.setState({ mouseOver: false });
  };

  render() {
    let left = this.props.left;
    let width = this.props.width;

    const objectTypeConfig = this.props.getObjectTypeConfig(this.props.task.objectType);
    
    let [adjustedStartTime, adjustedEndTime] = ensureStartAndEnd(this.props.task.start, this.props.task.end, startOfDay(Date.now()), this.props.task.duration, objectTypeConfig.durationUnit);
    const startDateMissing = this.props.task.start === null;

    // Adjust according to current drag to preview changes
    if (this.props.task.id === this.props.dragTaskId && this.props.dragDeadZoneReached) {
      // Move task
      if (this.props.dragType === DRAG_MOVE) {
        const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(this.props.xDelta, this.props.zoomMode, this.props.hourWidth);
        const moveDurationHours = snappedMoveDeltaInPixels / this.props.hourWidth;
        left += snappedMoveDeltaInPixels;
        width += this.props.minWidthAdjustment;
        if (this.props.zoomMode !== ZOOM_MODE_DAY) {
          adjustedStartTime = addDays(adjustedStartTime, Math.round(moveDurationHours / 24));
          adjustedEndTime = addDays(adjustedEndTime, Math.round(moveDurationHours / 24));
        } else {
          adjustedStartTime = addHours(adjustedStartTime, moveDurationHours);
          adjustedEndTime = addHours(adjustedEndTime, moveDurationHours);
        }
        // Resize from start
      } else if (this.props.dragType === DRAG_RESIZE_START) {
        const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(this.props.xDelta, this.props.zoomMode, this.props.hourWidth);
        const moveDurationHours = snappedMoveDeltaInPixels / this.props.hourWidth;
        left += snappedMoveDeltaInPixels;
        width = Math.max(width - snappedMoveDeltaInPixels, 0);

        if (this.props.zoomMode !== ZOOM_MODE_DAY) {
          adjustedStartTime = addDays(adjustedStartTime, Math.round(moveDurationHours / 24));
        } else {
          adjustedStartTime = addHours(adjustedStartTime, moveDurationHours);
        }
        // Resize from end
      } else if (this.props.dragType === DRAG_RESIZE_END) {
        const snappedMoveDeltaInPixels = snapMouseMoveBasedOnZoomMode(
          this.props.xDelta + this.props.minWidthAdjustment,
          this.props.zoomMode,
          this.props.hourWidth
        );
        const moveDurationHours = snappedMoveDeltaInPixels / this.props.hourWidth;
        width = Math.max(width + snappedMoveDeltaInPixels, 0);
        if (this.props.zoomMode !== ZOOM_MODE_DAY) {
          adjustedEndTime = addDays(adjustedEndTime, Math.round(moveDurationHours / 24));
        } else {
          adjustedEndTime = addHours(adjustedEndTime, moveDurationHours);
        }
      } else {
        width += this.props.minWidthAdjustment;
      }
    } else {
      width += this.props.minWidthAdjustment;
    }

    const useFullOpacity =
      this.props.isSelected ||
      (this.state.mouseOver && !this.props.dragUnderway) ||
      (this.state.mouseOver && this.props.dragUnderway && this.props.dragTaskId === this.props.task.id);

    const barHeight = this.props.taskHeight === TASK_HEIGHT_LOOSE ? 21 : 17;
    const top = Math.floor(this.props.taskHeight * this.props.index + (this.props.taskHeight - barHeight) / 2);
    const bgColor = hexToRGB(this.props.color, useFullOpacity ? 1 : 0.5);
    const stripedColor = brightenHexColor(this.props.color, 64, useFullOpacity ? 1 : 0.5);
    const checkedoutBackground =
      "repeating-linear-gradient(45deg, " + bgColor + ", " + bgColor + " 10px, " + stripedColor + " 10px, " + stripedColor + " 20px)";
    const noStartDateBackground =
      "repeating-linear-gradient(135deg, " + bgColor + ", " + bgColor + " 5px, " + stripedColor + " 5px, " + stripedColor + " 10px)";
    return (
      <DataTaskRoot
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        style={{
          left: left - DATA_TASK_SIDE_EXTRA, 
          width: width + 2 * DATA_TASK_SIDE_EXTRA,
          height: barHeight,
          top: top,
        }}
      >
        <WrappedTooltip
          task={this.props.task}
          dragUnderway={this.props.dragUnderway}
        >
          <DataTaskInner
            editable={!this.props.checkedOutToSomeoneElse && this.props.editable}
            background={this.props.checkedOutToSomeoneElse ? checkedoutBackground : startDateMissing ? noStartDateBackground : bgColor}
            onClick={this.selectItem}
            width={width}
            height={barHeight}
          >
            {this.getTaskContents(width, useFullOpacity, adjustedStartTime, adjustedEndTime)}
          </DataTaskInner>
        </WrappedTooltip>
      </DataTaskRoot>
    );
  }
}

const mapState = (state: iRootState, ownProps: IDataTaskProps) => ({
  isSelected: state.items.selectedItem === ownProps.task.id,
  taskHeight: state.UI.displaySettings.taskHeight,
  dateFormat: state.UI.displaySettings.dateFormat,
  timeFormat: state.UI.displaySettings.timeFormat,
  showTimeInTable: state.UI.displaySettings.showTimeInTable,
  linkConfigurations: state.common.configuration.linkConfigurations,
  getItem: (itemId: number) => state.items.allData[itemId],
  getObjectTypeConfig: (objectTypeId: number) => Array.from(state.common.configuration.objectTypeConfigurations.values()).find(i => i.objectTypeId === objectTypeId),
});

const mapDispatch = (dispatch: Dispatch) => ({
  onSelectItem: (taskId: number) => dispatch.items.selectItem(taskId),
  toggleShowEditBox: dispatch.UI.toggleShowEditBox,
});

export default connect(mapState, mapDispatch)(DataTask);
